import { LiveKitRoom, VideoConference, useLocalParticipant, useRoomContext } from '@livekit/components-react';
import '@livekit/components-styles';
import React from 'react';

const VirtualClassRoom = ({ token, roomName, onDisconnect, audioEnabled = false }) => {
    // Use SoftEx LiveKit URL
    const serverUrl = 'https://livekit.softextensions.com';

    if (!token) {
        return <div>Loading Class...</div>;
    }

    return (
        <LiveKitRoom
            video={true}
            audio={audioEnabled}
            token={token}
            serverUrl={serverUrl}
            connect={true}
            simulcast={true}
            adaptiveStream={true}
            dynacast={true}
            data-lk-theme="default"
            style={{ height: '100%', width: '100%' }}
            onDisconnected={onDisconnect}
            onMediaDeviceFailure={(err) => {
                console.error('Media device failure:', err);
                alert('Media device error: Please check if your camera/microphone are being used by another app or if permissions were revoked.');
            }}
        >
            <VideoConference />
            <RoomControls />
        </LiveKitRoom>
    );
};

// Hand Raise Control Component
const RoomControls = () => {
    const { localParticipant } = useLocalParticipant();
    const [handRaised, setHandRaised] = React.useState(false);
    const [notifications, setNotifications] = React.useState([]);
    const roomContext = useRoomContext();
    const room = roomContext ? roomContext.room : null;

    React.useEffect(() => {
        if (!room) return;

        const handleData = (payload, participant) => {
            const decoder = new TextDecoder();
            const strData = decoder.decode(payload);
            try {
                const data = JSON.parse(strData);
                if (data.type === 'hand_raise') {
                    showNotification(`${participant.identity} raised hand ✋`);
                }
            } catch (e) {
                console.error('Failed to parse data message', e);
            }
        };

        room.on('dataReceived', handleData);
        return () => {
            room.off('dataReceived', handleData);
        };
    }, [room]);

    const showNotification = (msg) => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, msg }]);
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 3000);
    };

    const toggleHand = () => {
        const newState = !handRaised;
        setHandRaised(newState);

        // Send data to room
        const data = JSON.stringify({ type: 'hand_raise', value: newState });
        const encoder = new TextEncoder();
        localParticipant.publishData(encoder.encode(data), { reliable: true });

        if (newState) {
            showNotification('You raised your hand ✋');
        }
    };

    return (
        <>
            <div style={{ position: 'absolute', bottom: '20px', left: '20px', zIndex: 50 }}>
                <button
                    onClick={toggleHand}
                    style={{
                        padding: '10px 15px',
                        borderRadius: '20px',
                        border: 'none',
                        backgroundColor: handRaised ? '#ffb700' : '#333',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                >
                    <span>✋</span>
                    {handRaised ? 'Lower Hand' : 'Raise Hand'}
                </button>
            </div>

            {/* Notification Overlay */}
            <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {notifications.map(n => (
                    <div key={n.id} style={{
                        padding: '12px 20px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        borderRadius: '8px',
                        backdropFilter: 'blur(4px)',
                        animation: 'fadeIn 0.3s ease-out'
                    }}>
                        {n.msg}
                    </div>
                ))}
            </div>
        </>
    );
};

export default VirtualClassRoom;
